import { useCallback } from 'react'
import { GameMode, GameState, MultiplayerState } from '../types'
import { Language, t } from '../data/translations'

const initialMultiplayerState: MultiplayerState = {
  localPlayerId: null,
  localPlayerNumber: null,
  roomInfo: null,
  connectionStatus: 'DISCONNECTED',
  gameSeed: null,
}

export const useConfirm = (
  params: {
    showConfirmDialog: { visible: boolean; message: string; onConfirm: () => void }
    setShowConfirmDialog: (v: { visible: boolean; message: string; onConfirm: () => void }) => void
    setShowInGameMenu: (v: boolean) => void
    setGameState: (v: GameState | null) => void
    setGameMode: (v: GameMode) => void
    handleQuit: () => void
    resetDragState?: () => void
    // Multiplayer cleanup functions
    leaveRoom?: (intentional?: boolean) => void
    closeWebRTC?: () => void
    setMultiplayerState?: (v: MultiplayerState) => void
    language: Language
    isMultiplayer?: boolean
    gameState?: GameState | null
  }
) => {
  const { showConfirmDialog, setShowConfirmDialog, setShowInGameMenu, setGameState, setGameMode, handleQuit, resetDragState, leaveRoom, closeWebRTC, setMultiplayerState, language, isMultiplayer, gameState } = params

  const handleShowConfirm = useCallback((message: string, onConfirm: () => void) => {
    setShowConfirmDialog({ visible: true, message, onConfirm })
  }, [setShowConfirmDialog])

  const handleHideConfirm = useCallback(() => {
    setShowConfirmDialog({ visible: false, message: '', onConfirm: () => { } })
  }, [setShowConfirmDialog])

  const handleConfirmAction = useCallback(() => {
    showConfirmDialog.onConfirm()
    handleHideConfirm()
  }, [showConfirmDialog, handleHideConfirm])

  const handleReturnToMainMenu = useCallback(() => {
    // Use abandonment warning for active multiplayer games
    const isActiveMultiplayerGame = isMultiplayer && gameState && gameState.phase !== 'GAME_OVER'
    const confirmMessage = isActiveMultiplayerGame
      ? t('multiplayer.abandonWarning', language)
      : t('multiplayer.confirmQuitMessage', language)

    handleShowConfirm(
      confirmMessage,
      () => {
        // Clean up multiplayer state
        if (leaveRoom) leaveRoom(true)
        if (closeWebRTC) closeWebRTC()
        if (setMultiplayerState) setMultiplayerState(initialMultiplayerState)

        setShowInGameMenu(false)
        setGameState(null)
        setGameMode('MAIN_MENU')
        if (resetDragState) resetDragState()
      }
    )
  }, [handleShowConfirm, setShowInGameMenu, setGameState, setGameMode, resetDragState, leaveRoom, closeWebRTC, setMultiplayerState, isMultiplayer, gameState, language])

  const handleQuitWithConfirm = useCallback(() => {
    handleShowConfirm(
      t('multiplayer.confirmQuitAppMessage', language),
      () => {
        handleQuit()
      }
    )
  }, [handleShowConfirm, handleQuit])

  return {
    handleShowConfirm,
    handleHideConfirm,
    handleConfirmAction,
    handleReturnToMainMenu,
    handleQuitWithConfirm,
  }
}

